import React, { useState } from "react";
import { Link } from "react-router-dom";
import { deleteDoc, doc } from "firebase/firestore";
import { useAuth } from "./auth/AuthContext";
import { db } from "./firebase";
import { useMyBabbleGames } from "./hooks/useMyBabbleGames";

export default function MyBabbleGames({
  title = "My Babble Games",
  headingLevel = 2,
  showStartLink = true,
  layout = "grid",
  style = {},
}) {
  const { user } = useAuth();
  const { games, loading, error } = useMyBabbleGames(user);
  const [deletingId, setDeletingId] = useState(null);
  const [localError, setLocalError] = useState(null);

  if (!user) {
    return (
      <section style={{ marginTop: 32, ...style }}>
        <p>Please sign in to manage Babble games.</p>
      </section>
    );
  }

  const HeadingTag = headingLevel === 3 ? "h3" : "h2";
  const cardsStyle = layout === "stack"
    ? { display: "flex", flexDirection: "column", gap: 12, marginTop: 12 }
    : { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16, marginTop: 16 };

  const cardStyle = layout === "stack"
    ? { border: "1px solid #ddd", borderRadius: 10, padding: 12 }
    : { border: "1px solid #e0e0e0", borderRadius: 12, padding: 16 };

  const handleDelete = async (game) => {
    if (!window.confirm(`Delete game "${game.title || "Babble game"}"? This cannot be undone.`)) return;
    setDeletingId(game.id);
    setLocalError(null);
    try {
      await deleteDoc(doc(db, "games_babble", game.id));
    } catch (err) {
      console.error("[BabbleGames] delete", err);
      setLocalError(err.message || "Failed to delete game.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <section style={{ marginTop: 32, ...style }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <HeadingTag style={{ margin: 0 }}>{title}</HeadingTag>
        {showStartLink && <Link to="/babble/new" style={{ fontSize: 14 }}>Start a new game →</Link>}
      </div>

      {(error || localError) && (
        <div style={{ border: "1px solid #e57373", background: "#ffebee", padding: 12, borderRadius: 8, marginTop: 12 }}>
          {localError || error}
        </div>
      )}

      {loading ? (
        <p style={{ marginTop: 12 }}>Loading games…</p>
      ) : games.length === 0 ? (
        <p style={{ marginTop: 12 }}>No games yet. Create one and share the link with a friend.</p>
      ) : (
        <div style={cardsStyle}>
          {games.map((game) => {
            const role = game.hostUid === user.uid ? "host" : "guest";
            const otherRole = role === "host" ? "guest" : "host";
            const myScore = Number(game.scores?.[role] || 0);
            const oppScore = Number(game.scores?.[otherRole] || 0);
            const isMyTurn = game.currentTurnUid === user.uid;
            const joined = role === "host" ? Boolean(game.guestUid) : Boolean(game.hostUid);
            return (
              <div key={game.id} style={{ ...cardStyle, display: "flex", flexDirection: "column", gap: 8 }}>
                <div>
                  <h3 style={{ margin: "0 0 4px", fontSize: layout === "stack" ? 16 : 18 }}>{game.title || "Babble game"}</h3>
                  <div style={{ fontSize: 13, opacity: 0.8 }}>
                    Role: {role} • Status: {game.status || "waiting"}
                  </div>
                </div>
                <div style={{ fontSize: 14 }}>
                  Score: <b>{myScore}</b> — {oppScore}
                </div>
                <div style={{ fontSize: 13, color: isMyTurn ? "#2e7d32" : "#555" }}>
                  {joined ? (isMyTurn ? "Your turn" : "Waiting for opponent") : "Waiting for opponent to join"}
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: "auto", flexWrap: "wrap" }}>
                  <Link to={`/babble/${game.id}`} style={{ flex: 1, textAlign: "center", padding: "8px 12px", border: "1px solid #1976d2", borderRadius: 8 }}>
                    Open game
                  </Link>
                  <button
                    onClick={() => handleDelete(game)}
                    disabled={deletingId === game.id}
                    style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid #d32f2f", background: "#fbe9e7", flexBasis: "auto" }}
                  >
                    {deletingId === game.id ? "Deleting…" : "Delete"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
